import { useState } from 'react'
import { Heading, Text, Button } from '@shinkansen/sakura'
import { 
  useMyAddresses, 
  useSaveAddress, 
  useDeleteAddress, 
  useSetDefaultAddress,
  type Address,
  type SaveAddressInput,
} from '../api/addresses'
import { LocationPicker, type Location } from '../components/map'
import styles from './addresses.module.css'

type ViewMode = 'list' | 'add' | 'edit'

export function AddressesPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [addressLabel, setAddressLabel] = useState('')
  const [location, setLocation] = useState<Location | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [addressPendingDelete, setAddressPendingDelete] = useState<Address | null>(null)

  const { data: addresses, isLoading } = useMyAddresses()
  const saveAddress = useSaveAddress()
  const deleteAddress = useDeleteAddress()
  const setDefaultAddress = useSetDefaultAddress()

  const handleAddNew = () => {
    setAddressLabel('')
    setLocation(null)
    setError(null)
    setViewMode('add')
  }

  const handleEdit = (address: Address) => {
    setAddressLabel(address.label || '')
    setLocation({
      lat: address.coordinates.lat,
      lng: address.coordinates.lng,
      address: address.fullAddress,
      notes: address.notes ?? undefined,
    })
    setError(null)
    setViewMode('edit')
  }

  const handleCancel = () => {
    setViewMode('list')
    setAddressLabel('')
    setLocation(null)
    setError(null)
  }

  const handleSave = async () => {
    if (!location) {
      setError('Please select a location on the map')
      return
    }

    try {
      const input: SaveAddressInput = {
        label: addressLabel || undefined,
        fullAddress: location.address,
        lat: location.lat,
        lng: location.lng,
        notes: location.notes,
        isDefault: addresses?.length === 0,
      }

      await saveAddress.mutateAsync(input)
      handleCancel()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save address')
    }
  }

  const handleDelete = (address: Address) => {
    if (address.isDefault && (addresses?.length ?? 0) > 1) {
      setError('Set another address as default before deleting this one.')
      return
    }

    setError(null)
    setAddressPendingDelete(address)
  }

  const confirmDelete = async () => {
    if (!addressPendingDelete) return

    try {
      await deleteAddress.mutateAsync(addressPendingDelete.id)
      setAddressPendingDelete(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete address')
    }
  }

  const handleSetDefault = async (id: string) => {
    try {
      await setDefaultAddress.mutateAsync(id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set default address')
    }
  }

  if (viewMode === 'add' || viewMode === 'edit') {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={handleCancel}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            BACK
          </button>
          <Heading level="h1" size="lg">
            {viewMode === 'add' ? 'ADD NEW ADDRESS' : 'EDIT ADDRESS'}
          </Heading>
        </div>

        {error && (
          <div className={styles.errorBanner}>{error}</div>
        )}

        <div className={styles.form}>
          <div className={styles.labelField}>
            <label className={styles.fieldLabel}>ADDRESS LABEL (OPTIONAL)</label>
            <input
              type="text"
              className={styles.input}
              value={addressLabel}
              onChange={(e) => setAddressLabel(e.target.value)}
              placeholder="e.g. Home, Office, Mom's House"
            />
          </div>

          <LocationPicker
            value={location || undefined}
            onChange={setLocation}
            showDetailFields={true}
          />

          <div className={styles.actions}>
            <Button variant="secondary" onClick={handleCancel}>
              CANCEL
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSave}
              disabled={!location || saveAddress.isPending}
            >
              {saveAddress.isPending ? 'SAVING...' : 'SAVE ADDRESS'}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Heading level="h1" size="lg">DELIVERY ADDRESSES</Heading>
        <Button variant="primary" onClick={handleAddNew}>
          + ADD NEW
        </Button>
      </div>

      {error && (
        <div className={styles.errorBanner}>{error}</div>
      )}

      {isLoading ? (
        <div className={styles.loading}>Loading addresses...</div>
      ) : addresses?.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
          </div>
          <Text size="lg">NO SAVED ADDRESSES</Text>
          <Text variant="muted">Add your first delivery address to get started</Text>
          <Button variant="primary" onClick={handleAddNew}>
            ADD YOUR FIRST ADDRESS
          </Button>
        </div>
      ) : (
        <div className={styles.addressList}>
          {addresses?.map((address, index) => (
            <div key={address.id} className={styles.addressCard}>
              <div className={styles.cardHeader}>
                <span className={styles.cardNumber}>{String(index + 1).padStart(2, '0')}</span>
                <div className={styles.cardLabels}>
                  {address.label && (
                    <span className={styles.labelBadge}>{address.label}</span>
                  )}
                  {address.isDefault && (
                    <span className={styles.defaultBadge}>DEFAULT</span>
                  )}
                </div>
              </div>
              
              <div className={styles.cardBody}>
                <p className={styles.fullAddress}>{address.fullAddress}</p>
                {address.notes && (
                  <p className={styles.notes}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                    </svg>
                    {address.notes}
                  </p>
                )}
              </div>

              <div className={styles.cardActions}>
                {!address.isDefault && (
                  <button 
                    className={styles.actionButton}
                    onClick={() => handleSetDefault(address.id)}
                    disabled={setDefaultAddress.isPending}
                  >
                    SET AS DEFAULT
                  </button>
                )}
                <button 
                  className={styles.actionButton}
                  onClick={() => handleEdit(address)}
                >
                  EDIT
                </button>
                <button 
                  className={styles.actionButtonDanger}
                  onClick={() => handleDelete(address)}
                  disabled={deleteAddress.isPending}
                >
                  DELETE
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {addressPendingDelete && (
        <div className={styles.modalOverlay} role="presentation" onClick={() => setAddressPendingDelete(null)}>
          <div
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-address-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <span className={styles.modalEyebrow}>DESTRUCTIVE ACTION</span>
              <h2 id="delete-address-title">Delete Address</h2>
            </div>
            <p className={styles.modalBodyText}>
              This will remove the saved delivery address
              {addressPendingDelete.label ? ` "${addressPendingDelete.label}"` : ''}. Existing orders keep their delivery details.
            </p>
            <div className={styles.modalAddress}>
              {addressPendingDelete.fullAddress}
            </div>
            <div className={styles.modalActions}>
              <button
                className={styles.modalSecondary}
                type="button"
                onClick={() => setAddressPendingDelete(null)}
              >
                KEEP ADDRESS
              </button>
              <button
                className={styles.modalDanger}
                type="button"
                onClick={confirmDelete}
                disabled={deleteAddress.isPending}
              >
                {deleteAddress.isPending ? 'DELETING...' : 'DELETE ADDRESS'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
